import {when, reaction, observable, computed, IObservableObject} from 'mobx'
import Cookies from 'js-cookie'
import { SubscriptionClient } from 'subscriptions-transport-ws'
import { DocumentNode, GraphQLError } from 'graphql'
import type {TypedDocumentNode} from '@graphql-typed-document-node/core'

import { onError } from '@apollo/client/link/error'
import { ApolloLink, execute, FetchResult } from '@apollo/client/link/core'
import { setContext } from '@apollo/client/link/context'
import { HttpLink } from '@apollo/client/link/http'
import { WebSocketLink } from '@apollo/client/link/ws'
import {getMainDefinition, Observable} from '@apollo/client/utilities'

import {ApolloClient, ApolloError, InMemoryCache, NormalizedCacheObject, QueryOptions} from '@apollo/client'

import { TypedTypePolicies } from './apollo-helpers'
import intr from './introspection-data'

function devtoolsOpened() {
    let opened = false
    const e = new Image()
    Object.defineProperty(e, 'id', {get() {opened = true}})
    console.log('%c', e)
    return opened
}

function extendOperationError(operationName: string, error: GraphQLError) {
    const errorType: string = error.extensions?.errorType ?? 'InternalServerError'
    const extendedError = new GraphQLError(
        error.message,
        error.nodes,
        error.source,
        error.positions,
        error.path,
        error.originalError,
        error.extensions
    )
    Object.assign(extendedError, error.extensions ?? {}, {errorType})
    console.warn(
        `[GraphQL error] ${operationName} throws ${errorType}: ${extendedError.message}\n`,
        extendedError
    )
    return extendedError
}

type IsSingleData<T> =
    T extends { data: any }
        ? Exclude<keyof T, 'data'> extends never
        ? true
        : false
        : false

export type Prepare<R> = IsSingleData<R> extends false ? R : R extends {data: infer D} ? D : never

export function prepare<R extends {[key: string]: any}>(
    val: FetchResult<R>
): Prepare<R> {
    if (val.errors?.length)
        throw val.errors[0]

    if (!val.data)
        throw new Error('Empty response!')

    const data = val.data
    if (Object.keys(data).length === 1 && 'data' in data)
        return data.data
    return data as any
}

const deduped: Set<DocumentNode> = new Set()

function dedupe(d: DocumentNode): DocumentNode {
    if (deduped.has(d))
        return d
    const names: string[] = []
    const res = []
    for (const def of d.definitions) {
        const name = 'name' in def ? def.name?.value : undefined
        if (name && !names.includes(name)) {
            res.push(def)
            names.push(name)
        }
    }
    (d as any).definitions = res
    deduped.add(d)
    return d
}

export class Client {
    private readonly _url: string
    @observable url: string
    @observable token: string | undefined
    @observable http = false
    @observable.ref lastError: any = null

    private sc: SubscriptionClient | undefined
    @observable.ref private link: ApolloLink | undefined = undefined
    @observable private _online = false
    @observable private connecting = false

    @observable emulatedOffline = false

    @observable.shallow uploads: Record<string, XMLHttpRequest> = {}

    apollo: ApolloClient<NormalizedCacheObject> | null = null

    subs: Map<IObservableObject, () => void> = new Map()

    constructor(url: string, private readonly headers: Record<string, string> = {}) {
        this.token = Cookies.get('token')
        this.http = !!localStorage.getItem('useHttp')
        this._url = url
        this.url = localStorage.getItem('backendUrl') || url
        // эти события работают, например, когда переключаешь галочку "offline" в dev tools
        // но на вебсокеты они не влияют, да и online не значит что есть интернет
        // так что пригождаются исключительно для отладки
        window.addEventListener('online', () => {
            if (devtoolsOpened())
                this.emulateOffline(false)
        })
        window.addEventListener('offline', () => {
            if (devtoolsOpened())
                this.emulateOffline(true)
        })
        reaction(() => this.token, token => {
            if (token)
                Cookies.set('token', token, {expires: new Date(2222, 10)})
            else
                Cookies.remove('token')
        })
        reaction(() => this.url, () => {
            this.disconnect()
            void this.connect()
        })
    }

    emulateOffline(off: boolean) {
        this.emulatedOffline = off
        if (off) {
            const conn = this.connected
            this.disconnect()
            if (conn)
                this.connect().catch(() => {/*ok*/})
        }
    }

    useHttp(http = true) {
        if (http)
            localStorage.setItem('useHttp', '1')
        else
            localStorage.removeItem('useHttp')
        this.http = http
    }

    useUrl(url?: string) {
        if (url)
            localStorage.setItem('backendUrl', url)
        else
            localStorage.removeItem('backendUrl')
        this.url = url || this._url
    }

    @computed
    get outdated() {
        const err = this.lastError
        return !!err && err.message === 'version mismatch'
    }

    @computed
    get auth() {
        return Object.assign({}, this.headers, this.token ? {Authorization: 'Bearer ' + this.token} : {})
    }

    @computed
    get connected() {
        return !!this.link || this.connecting
    }

    @computed
    get online() {
        return this.connected && this._online
    }

    private onDisconnect = () => {
        this._online = false
        if (!this.sc)
            this.link = undefined
    }

    private onReconnect = () => this._online = true

    async connect() {
        if (this.online)
            return
        if (this.connected)
            await when(() => !this.connected || this.online)
        if (this.online)
            return

        this.connecting = true
        if (this.emulatedOffline)
            await when(() => !this.emulatedOffline)
        this.sc = await new Promise<SubscriptionClient>((resolve, reject) => {
            const sc: SubscriptionClient = new SubscriptionClient(
                this.url.replace('http', 'ws'),
                {
                    minTimeout: 3000,
                    timeout: 30000,
                    reconnect: true,
                    connectionParams: () => this.auth,
                    lazy: false,
                    connectionCallback: err => {
                        if (err) {
                            this.lastError = err
                            reject([err, sc])
                        } else {
                            this.lastError = null
                            resolve(sc)
                        }
                    },
                },
            )
        }).catch(([e, sc]) => {
            sc.close()
            this.connecting = false
            throw e
        })

        this.sc.onDisconnected(this.onDisconnect)
        this.sc.onReconnected(this.onReconnect)

        this.link = ApolloLink.from([
            onError(({graphQLErrors, networkError, operation}) => {
                if (networkError)
                    console.error('[Network error]', operation.operationName, networkError)
                if (!graphQLErrors)
                    return
                return Observable.of({
                    errors: graphQLErrors.map(error => extendOperationError(operation.operationName, error)),
                })
            }),
            ApolloLink.split(
                ({query}) => {
                    const def = getMainDefinition(query)
                    return !this.http || (def.kind === 'OperationDefinition' && def.operation === 'subscription')
                },
                new WebSocketLink(this.sc),
                setContext((_, {headers}) => ({
                    headers: {
                        ...headers,
                        ...this.auth,
                    },
                })).concat(new HttpLink({uri: this.url})),
            ),
        ])
        const typePolicies: TypedTypePolicies = {
            Query: {
                fields: {
                    review(_, { args, toReference }) {
                        return toReference({
                            __typename: 'Review',
                            id: args?.id,
                        })
                    },
                    user(q, {args, toReference, canRead}) {
                        const active = toReference({__typename: 'ActiveUser', id: args?.id})
                        const invited = toReference({__typename: 'InvitedUser', id: args?.id})
                        if (canRead(active))
                            return active
                        if (canRead(invited))
                            return invited
                        return q
                    },
                },
            },
            ActiveUser: {
                fields: {
                    reviews: {merge: false},
                },
            },
            InvitedUser: {
                fields: {
                    reviews: {merge: false},
                },
            },
        }
        this.apollo = new ApolloClient({
            link: this.link,
            cache: new InMemoryCache({
                possibleTypes: intr.possibleTypes,
                typePolicies,
            }),
        })
        this._online = true
        this.connecting = false
    }

    disconnect() {
        if (!this.link)
            return
        const sc = this.sc
        delete this.sc
        sc?.close()
    }

    do<Result=any>(query: DocumentNode, variables: Record<string, any> = {}) {
        if (!this.link || !this._online)
            throw new Error('Connect first!')
        const o = execute(this.link, {query: dedupe(query), variables})
        return new Promise<FetchResult<Result>>((resolve, reject) => {
            const off = when(() => !this.online, () => reject('offline'))
            o.subscribe({
                next: r => {
                    off()
                    resolve(r as any)
                },
                error: reject,
            })
        })
    }

    send<R, V>(document: TypedDocumentNode<R, V>, variables: V): Promise<Prepare<R>> {
        return this.do<R>(document, variables).then(prepare)
    }

    async query<R, V>(query: TypedDocumentNode<R, V>, variables: V, options?: Omit<QueryOptions<V, R>, 'query' | 'variables'>): Promise<Prepare<R>> {
        if (!this.apollo)
            throw new Error('Connect first!')
        query = dedupe(query)
        if (this.emulatedOffline) {
            const cached = this.apollo.readQuery({query, variables})
            if (cached)
                return prepare({data: cached})
            await when(() => !this.emulatedOffline)
        }
        return this.apollo.query({query, variables, ...options}).then(prepare)
    }

    async watch<R, V>(query: TypedDocumentNode<R, V>, variables: V, onError?: (error: any) => void): Promise<Prepare<R>> {
        if (!this.apollo)
            throw new Error('Connect first!')
        query = dedupe(query)
        if (this.emulatedOffline)
            await when(() => !this.emulatedOffline)
        const q = this.apollo.watchQuery({query, variables})
        const res = await q.result().then(prepare)
        const obs = observable.object(res)
        const s = q.map(prepare).subscribe(value => {
            Object.assign(obs, value)
        }, onError)
        this.subs.set(obs, () => s.unsubscribe())
        return obs
    }

    unwatch(obj: Record<string, unknown>) {
        const unsub = this.subs.get(obj as any)
        if (!unsub)
            throw new Error('Not a sub!')
        this.subs.delete(obj as any)
        unsub()
    }

    async watchEntity<R, V>(query: TypedDocumentNode<R, V>, variables: V): Promise<Prepare<R>> {
        return this.watch(query, variables, e => console.warn('!!!', e))
    }


    async mutate<R, V>(mutation: TypedDocumentNode<R, V>, variables: V): Promise<Prepare<R>> {
        if (!this.apollo)
            throw new Error('Connect first!')
        mutation = dedupe(mutation)
        return this.apollo.mutate({mutation, variables}).then(prepare, e => {
            if (e instanceof ApolloError && e.graphQLErrors.length > 0)
                throw e.graphQLErrors[0]
            throw e
        })
    }

    sub<R, V>(
        query: TypedDocumentNode<R, V>,
        variables: V,
        cb: (value: Prepare<R>) => void,
    ): () => void {
        if (!this.apollo)
            throw new Error('Connect first!')
        const s = this.apollo.subscribe<R, V>({query: dedupe(query), variables}).subscribe(res => cb(prepare(res)))
        return () => s.unsubscribe()
    }

    removeCached(ent: {__typename: string, id: string}) {
        this.apollo?.cache.evict({id: ent.__typename + ':' + ent.id})
        this.apollo?.cache.gc()
    }

    subscribe<Result=any>(
        query: DocumentNode,
        variables: Record<string, any>,
        cb: (value: FetchResult<Result>) => void,
        error: (error: any) => void
    ) {
        if (!this.link || !this._online)
            throw new Error('Connect first!')
        const sub = execute(this.link, {query: dedupe(query), variables}).subscribe({next: cb, error})
        const off = when(() => !this.online, () => {
            sub.unsubscribe()
            error('offline')
        })
        return () => {
            off()
            sub.unsubscribe()
        }
    }

    doFile(
        url: string,
        file: File,
        {id, onProgress, fields = {}}: {
            id?: string
            onProgress?: (e: ProgressEvent) => void
            fields?: Record<string, string>
        } = {},
    ): Promise<void> {
        if (!this.link || !this._online)
            throw new Error('Connect first!')

        const xhr = new XMLHttpRequest()
        xhr.open('POST', url, true)
        if (onProgress)
            xhr.upload.onprogress = onProgress
        if (id)
            this.uploads[id] = xhr

        const off = when(() => this.emulatedOffline, () => xhr.abort())

        const cleanup = () => {
            off()
            if (id)
                delete this.uploads[id]
        }

        return new Promise<void>((resolve, reject) => {
            xhr.onload = () => resolve()
            xhr.onerror = () => reject(new Error('UploadError'))
            xhr.onabort = () => reject(new Error(this.emulatedOffline ? 'UploadError' : 'UploadAborted'))

            const form = new FormData()
            for (const [k, v] of Object.entries(fields))
                form.append(k, v)
            form.append('file', file, file.name)
            xhr.send(form)
        }).then(() => {
            cleanup()
        }).catch(e => {
            cleanup()
            throw e
        })
    }

    cancelFile(id: string) {
        const xhr = this.uploads[id]
        if (!xhr)
            return false

        xhr.abort()
        xhr.upload.onprogress = null
        delete this.uploads[id]
        return true
    }

    setHeader(name: string, value: string) {
        this.headers[name] = value
    }
}

let client!: Client

export function setClient(c: Client): Client {
    client = c
    return client
}

export {client as default}
